import React, { FC, useCallback, useEffect, useState } from 'react';
import { animated, to, useSpring } from '@react-spring/web';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as MinusIcon } from '../../../assets/icons/minus.svg';
import styles from './Cart.module.scss';
import { OrderableDish, useCart } from '../../../utils/hook/useCart';
import { useIsDesktop } from '../../../utils/hook/useIsDesktop';
import { vw } from '../../../utils/tool/vw';

export const MAX_QUANTITY_PER_ITEM = 50;

export type QuantityControlVariant = 'card' | 'detail' | 'drawer';

interface IProps {
    dish: OrderableDish;
    variant: QuantityControlVariant;
}

const SPRING = { tension: 340, friction: 28 };
const POP = { tension: 420, friction: 16 };

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function metrics(variant: QuantityControlVariant, isDesktop: boolean) {
    if (variant === 'detail') {
        const unit = isDesktop ? 38 : vw(38);
        return { collapsed: unit, expanded: isDesktop ? 110 : vw(110) };
    }
    if (variant === 'drawer') {
        const unit = isDesktop ? 28 : vw(28);
        return { collapsed: unit, expanded: isDesktop ? 96 : vw(96) };
    }
    const unit = isDesktop ? 42 : vw(32);
    return { collapsed: unit, expanded: isDesktop ? 108 : vw(80) };
}

function usePressSpring(reduceMotion: boolean) {
    const [spring, api] = useSpring(() => ({ scale: 1 }));
    const bind = {
        onPointerDown: () => {
            void api.start({ scale: 0.88, immediate: reduceMotion, config: SPRING });
        },
        onPointerUp: () => {
            void api.start({ scale: 1, immediate: reduceMotion, config: SPRING });
        },
        onPointerLeave: () => {
            void api.start({ scale: 1, immediate: reduceMotion, config: SPRING });
        },
        onPointerCancel: () => {
            void api.start({ scale: 1, immediate: reduceMotion, config: SPRING });
        },
    };
    return { spring, bind };
}

export const QuantityControl: FC<IProps> = ({ dish, variant }) => {
    const { add, setQuantity, quantityOf } = useCart();
    const qty = quantityOf(dish.id);
    const isDesktop = useIsDesktop();
    const [reduceMotion, setReduceMotion] = useState(prefersReducedMotion);
    const expanded = variant === 'drawer' || qty > 0;
    const { collapsed, expanded: expandedWidth } = metrics(variant, isDesktop);
    const minusPress = usePressSpring(reduceMotion);
    const plusPress = usePressSpring(reduceMotion);

    useEffect(() => {
        const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = (event: MediaQueryListEvent) => setReduceMotion(event.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    const shell = useSpring({
        width: expanded ? expandedWidth : collapsed,
        immediate: reduceMotion,
        config: SPRING,
    });

    const extras = useSpring({
        opacity: expanded ? 1 : 0,
        scale: expanded ? 1 : 0.85,
        immediate: reduceMotion,
        config: SPRING,
    });

    const addIcon = useSpring({
        cartOpacity: expanded ? 0 : 1,
        cartScale: expanded ? 0.85 : 1,
        plusOpacity: expanded ? 1 : 0,
        plusScale: expanded ? 1 : 0.85,
        immediate: reduceMotion,
        config: SPRING,
    });

    const [qtyPop, qtyPopApi] = useSpring(() => ({ scale: 1 }));
    const [plusPop, plusPopApi] = useSpring(() => ({ scale: 1 }));

    useEffect(() => {
        if (qty < 1 || reduceMotion) return;
        void qtyPopApi.start({ from: { scale: 1.18 }, to: { scale: 1 }, config: POP });
        if (qty > 1) {
            void plusPopApi.start({ from: { scale: 1.18 }, to: { scale: 1 }, config: POP });
        }
    }, [qty, qtyPopApi, plusPopApi, reduceMotion]);

    const onPlus = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (qty === 0) {
            add(dish);
            return;
        }
        if (qty >= MAX_QUANTITY_PER_ITEM) return;
        setQuantity(dish.id, qty + 1);
    }, [add, dish, qty, setQuantity]);

    const onMinus = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (qty < 1) return;
        setQuantity(dish.id, qty - 1);
    }, [dish.id, qty, setQuantity]);

    return (
        <animated.div
            className={`${styles.quantityControl} ${styles[`quantityControl_${variant}`]}${expanded ? '' : ` ${styles.quantityControlCollapsed}`}`}
            style={{ width: shell.width }}
            onClick={(event) => event.stopPropagation()}
        >
            <animated.button
                type="button"
                className={styles.quantityBtn}
                onClick={onMinus}
                tabIndex={expanded ? 0 : -1}
                aria-hidden={!expanded}
                aria-label={`Remove one ${dish.name}`}
                style={{
                    opacity: extras.opacity,
                    transform: extras.scale.to((value) => `scale(${value})`),
                    pointerEvents: expanded ? 'auto' : 'none',
                }}
                {...minusPress.bind}
            >
                <animated.span
                    className={styles.quantityBtnFace}
                    style={{ transform: minusPress.spring.scale.to((value) => `scale(${value})`) }}
                >
                    <MinusIcon className={styles.quantityIcon} />
                </animated.span>
            </animated.button>

            <animated.span
                className={styles.quantityValue}
                aria-live="polite"
                aria-hidden={qty === 0}
                style={{
                    opacity: extras.opacity,
                    transform: qtyPop.scale.to((value) => `scale(${value})`),
                }}
            >
                {qty}
            </animated.span>

            <button
                type="button"
                className={styles.quantityBtn}
                onClick={onPlus}
                aria-label={qty === 0 ? `Add ${dish.name} to order` : `Add one ${dish.name}`}
                disabled={qty >= MAX_QUANTITY_PER_ITEM}
                {...plusPress.bind}
            >
                <span className={styles.quantityBtnIcons}>
                    <animated.span
                        className={styles.quantityBtnFace}
                        style={{
                            opacity: addIcon.cartOpacity,
                            transform: to(
                                [addIcon.cartScale, plusPress.spring.scale],
                                (scale, press) => `scale(${scale * press})`
                            ),
                        }}
                    >
                        <PlusIcon className={styles.quantityPlusIcon} />
                    </animated.span>
                    <animated.span
                        className={styles.quantityBtnFace}
                        style={{
                            opacity: addIcon.plusOpacity,
                            transform: to(
                                [addIcon.plusScale, plusPop.scale, plusPress.spring.scale],
                                (scale, pop, press) => `scale(${scale * pop * press})`
                            ),
                        }}
                    >
                        <PlusIcon className={styles.quantityPlusIcon} />
                    </animated.span>
                </span>
            </button>
        </animated.div>
    );
};
